import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Play } from "lucide-react";
import { BOOK, STORIES, NARRATORS } from "../data/book";
import { useLang } from "../i18n";
import NewsletterForm from "../components/NewsletterForm";
import Marquee from "../components/Marquee";
import FlashPromo from "../components/FlashPromo";
import Reveal from "../components/Reveal";
import VinylCover from "../components/VinylCover";
import StoryCard from "../components/StoryCard";
import TeaserPlayer from "../components/TeaserPlayer";

const lineAnim = (i) => ({
  initial: { y: "115%" },
  animate: { y: 0 },
  transition: { duration: 1, delay: 0.15 + i * 0.14, ease: [0.25, 1, 0.5, 1] },
});

const Home = () => {
  const { lang, t } = useLang();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const coverY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const titleY = useTransform(scrollYProgress, [0, 1], [0, -80]);

  const featured = STORIES.slice(1, 7);
  const shortStories = [STORIES[1], STORIES[5], STORIES[18]];

  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen flex flex-col justify-center overflow-hidden pt-16">
        <div className="orb absolute -top-24 -left-24 w-[480px] h-[480px] rounded-full bg-ember/20" />
        <div className="orb absolute bottom-0 right-0 w-[420px] h-[420px] rounded-full bg-viola/20" style={{ animationDelay: "-5s" }} />
        <div className="orb absolute top-1/3 left-1/2 w-[300px] h-[300px] rounded-full bg-gold/10" style={{ animationDelay: "-8s" }} />

        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 w-full pt-8">
          <FlashPromo />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 py-20 grid lg:grid-cols-[1.2fr_1fr] gap-14 items-center w-full">
          <motion.div style={{ y: titleY }}>
            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.8 }}
              className="font-mono2 text-xs sm:text-sm tracking-[0.35em] uppercase text-ember mb-6"
              data-testid="hero-kicker"
            >
              {t("hero_kicker")}
            </motion.p>

            <h1 className="font-display font-extrabold uppercase leading-[0.9] tracking-tight text-glow-orange" data-testid="hero-title">
              {["Una", "vita", "da DJ"].map((line, i) => (
                <span key={line} className="masked">
                  <motion.span {...lineAnim(i)} className="block text-[19vw] sm:text-[13vw] lg:text-[8.5rem]">
                    {line}
                  </motion.span>
                </span>
              ))}
            </h1>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.75, duration: 1 }}
              className="font-serif-it text-2xl sm:text-3xl text-gold text-glow-gold mt-3"
              data-testid="hero-subtitle"
            >
              — {BOOK.subtitle[lang]}
            </motion.p>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.95, duration: 0.9 }}
              className="text-mist max-w-md mt-6 text-sm sm:text-base leading-relaxed"
            >
              {t("hero_tag")}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.1, duration: 0.9 }}
              className="flex flex-wrap gap-4 mt-9"
            >
              <Link
                to="/storie"
                data-testid="hero-cta-read"
                className="inline-flex items-center gap-2 rounded-full bg-ember px-7 py-3.5 text-sm font-semibold uppercase tracking-wider text-black hover:bg-gold hover:gap-3.5 transition-all duration-300"
              >
                {t("hero_cta1")} <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="#shorts"
                data-testid="hero-cta-shorts"
                className="inline-flex items-center gap-2 rounded-full border border-white/20 px-7 py-3.5 text-sm font-semibold uppercase tracking-wider hover:border-ember hover:text-ember transition-colors duration-300"
              >
                <Play className="w-4 h-4" /> {t("hero_cta2")}
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.3, duration: 1 }}
              className="flex flex-wrap gap-x-8 gap-y-3 mt-12 font-mono2 text-[10px] sm:text-xs tracking-[0.25em] uppercase text-mute"
              data-testid="hero-stats"
            >
              <span><b className="text-white">28</b> {t("stat_stories")}</span>
              <span><b className="text-white">26</b> {t("stat_chapters")}</span>
              <span>{t("stat_cities")}</span>
              <span><b className="text-white">360+</b> {t("stat_pages")}</span>
            </motion.div>
          </motion.div>

          <motion.div
            style={{ y: coverY }}
            initial={{ opacity: 0, x: 60 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5, duration: 1.2, ease: [0.25, 1, 0.5, 1] }}
          >
            <VinylCover />
          </motion.div>
        </div>
      </section>

      {/* TRAILER */}
      <section className="max-w-5xl mx-auto px-5 sm:px-8 py-24 sm:py-28" data-testid="trailer-section">
        <Reveal className="mb-10 text-center">
          <p className="font-mono2 text-xs tracking-[0.35em] uppercase text-ember mb-5">{t("trailer_kicker")}</p>
          <h2 className="font-display font-bold text-2xl sm:text-4xl lg:text-5xl leading-tight tracking-tight">{t("trailer_title")}</h2>
          <p className="text-mist mt-4 max-w-xl mx-auto text-sm sm:text-base">{t("trailer_sub")}</p>
        </Reveal>
        <Reveal>
          <div className="relative rounded-2xl border border-ember/30 overflow-hidden glow-orange">
            <video
              data-testid="trailer-video"
              key={lang}
              src={`${process.env.REACT_APP_BACKEND_URL}/api/files/trailer${["es", "zh"].includes(lang) ? `_${lang}` : ""}.mp4`}
              poster={`${process.env.REACT_APP_BACKEND_URL}/api/files/trailer${["es", "zh"].includes(lang) ? `_${lang}` : ""}.jpg`}
              controls
              loop
              playsInline
              preload="metadata"
              className="w-full aspect-video object-cover bg-black"
            />
          </div>
        </Reveal>
      </section>

      <Marquee text={t("marquee")} />

      {/* NOVEL */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-32" data-testid="novel-section">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <p className="font-mono2 text-xs tracking-[0.35em] uppercase text-ember mb-5">{t("novel_kicker")}</p>
            <h2 className="font-display font-bold text-2xl sm:text-4xl lg:text-5xl leading-tight tracking-tight mb-8">
              {t("novel_title")}
            </h2>
            <p className="text-mist leading-relaxed text-sm sm:text-base max-w-xl">{t("novel_text")}</p>
            <div className="mt-10 flex items-center gap-5">
              <img src="/img/author.png" alt="Andrea Perocchi alla consolle" className="w-16 h-16 rounded-full object-cover border border-ember/40" />
              <div>
                <p className="font-display font-bold">{BOOK.author}</p>
                <p className="font-mono2 text-[10px] tracking-[0.25em] uppercase text-mute">{t("author_role")} · {BOOK.publisher} {BOOK.year}</p>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="relative rounded-xl overflow-hidden border border-white/10">
              <img src="/img/cover_p0.png" alt="Copertina completa del libro" className="w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-transparent" />
              <p className="absolute bottom-4 left-5 font-serif-it text-lg text-gold">«{t("hero_sub")}»</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* STORIES MANIFESTO */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-28" data-testid="stories-section">
        <Reveal className="mb-14">
          <p className="font-mono2 text-xs tracking-[0.35em] uppercase text-ember mb-5">{t("stories_kicker")}</p>
          <h2 className="font-display font-bold text-2xl sm:text-4xl lg:text-5xl leading-tight tracking-tight max-w-3xl">
            {t("stories_title")}
          </h2>
          <p className="text-mist mt-5 max-w-xl text-sm sm:text-base">{t("stories_sub")}</p>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {featured.map((s, i) => (
            <Reveal key={s.id} delay={i * 0.07}>
              <StoryCard story={storySafe(s)} />
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-12 text-center">
          <Link
            to="/storie"
            data-testid="stories-view-all"
            className="inline-flex items-center gap-2 rounded-full border border-ember/50 px-8 py-3.5 text-sm font-semibold uppercase tracking-wider text-ember hover:bg-ember hover:text-black transition-all duration-300"
          >
            {t("stories_all")} — 28 <ArrowRight className="w-4 h-4" />
          </Link>
        </Reveal>
      </section>

      {/* VIDEO SHORTS */}
      <section id="shorts" className="max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-28" data-testid="shorts-section">
        <Reveal className="mb-12">
          <p className="font-mono2 text-xs tracking-[0.35em] uppercase text-ember mb-5">{t("shorts_kicker")}</p>
          <h2 className="font-display font-bold text-2xl sm:text-4xl lg:text-5xl leading-tight tracking-tight">{t("shorts_title")}</h2>
          <p className="text-mist mt-5 max-w-xl text-sm sm:text-base">{t("shorts_sub")}</p>
        </Reveal>
        <Reveal>
          <TeaserPlayer story={STORIES[1]} />
        </Reveal>
        <div className="grid sm:grid-cols-2 gap-5 mt-6">
          {shortStories.map((s, i) => {
            const nar = NARRATORS[s.narrator];
            return (
              <Reveal key={s.id} delay={i * 0.08} className={i === 2 ? "sm:col-span-2" : ""}>
                <Link
                  to={`/storia/${s.id}`}
                  data-testid={`short-card-${s.num}`}
                  className="group flex items-center justify-between rounded-xl border border-white/10 bg-card px-6 py-5 hover:border-ember/50 hover:bg-cardhover transition-all duration-500"
                >
                  <div className="flex items-center gap-5">
                    <span className="w-11 h-11 rounded-full border border-ember/50 flex items-center justify-center group-hover:bg-ember transition-colors duration-300">
                      <Play className="w-4 h-4 text-ember group-hover:text-black fill-current" />
                    </span>
                    <div>
                      <p className="font-display font-bold">{t("player_short")} · {s.num}</p>
                      <p className="font-mono2 text-[10px] tracking-[0.2em] uppercase" style={{ color: nar.color }}>
                        {nar[lang] ?? nar.it} · {s.place}
                      </p>
                    </div>
                  </div>
                  <span className="hidden sm:flex items-end gap-[3px] h-6">
                    {[0, 1, 2, 3, 4].map((b) => (
                      <span key={b} className="eq-bar w-1 h-full" style={{ background: nar.color, animationDelay: `${b * 0.14}s`, animationDuration: `${0.5 + b * 0.1}s` }} />
                    ))}
                  </span>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </section>

      <Marquee text={t("marquee")} />

      {/* SUPPORT STRIP */}
      <section className="border-y border-gold/20 bg-gold/5" data-testid="support-strip">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 flex flex-col md:flex-row items-center justify-between gap-8">
          <Reveal>
            <p className="font-mono2 text-xs tracking-[0.35em] uppercase text-gold mb-3">{t("support_kicker")}</p>
            <h2 className="font-display font-bold text-2xl sm:text-4xl tracking-tight">{t("support_title")}</h2>
            <p className="text-mist mt-3 max-w-lg text-sm sm:text-base">{t("support_sub")}</p>
          </Reveal>
          <Reveal delay={0.15}>
            <Link
              to="/sostieni"
              data-testid="support-cta"
              className="inline-flex items-center gap-2 rounded-full bg-gold px-8 py-4 text-sm font-semibold uppercase tracking-wider text-black hover:bg-ember transition-colors duration-300 whitespace-nowrap"
            >
              {t("support_cta")} <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      <NewsletterForm />

      {/* AUTHOR TEASER */}<section className="max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-32" data-testid="author-teaser">
        <div className="grid lg:grid-cols-[1fr_1.3fr] gap-14 items-center">
          <Reveal>
            <div className="relative rounded-xl overflow-hidden border border-white/10 max-w-md">
              <img src="/img/author.png" alt="Andrea Perocchi" className="w-full object-cover aspect-square" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-transparent to-transparent" />
              <p className="absolute bottom-4 left-5 font-mono2 text-[10px] tracking-[0.3em] uppercase text-ember">{t("author_role")}</p>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="font-mono2 text-xs tracking-[0.35em] uppercase text-ember mb-5">{t("author_kicker")}</p>
            <h2 className="font-display font-bold text-2xl sm:text-4xl lg:text-5xl leading-tight tracking-tight mb-7">{t("author_title")}</h2>
            <p className="text-mist leading-relaxed text-sm sm:text-base mb-9 max-w-xl">{t("author_p1")}</p>
            <Link
              to="/autore"
              data-testid="author-teaser-cta"
              className="inline-flex items-center gap-2 rounded-full bg-ember px-7 py-3.5 text-sm font-semibold uppercase tracking-wider text-black hover:bg-gold transition-colors duration-300"
            >
              {t("author_cta")} <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

const storySafe = (s) => s;

export default Home;
